/**
 * Registration-confirmation popup for the SwissPass canceled-after-registration
 * return path.
 *
 * This overlay is mounted inside {@link Home}. When the user lands on
 * `/login/swisspass/canceled_after_registration`, the router redirects to
 * `/home` with `location.state.showSwissPassPostRegistration = true`, which
 * causes this popup to open. The transient state is cleared immediately so it
 * does not reappear on a page refresh.
 */

import { useIonToast } from "@ionic/react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { startSwissPassLoginWithStateSave } from "../services/auth";
import { getUIColorPalette } from "../theme/ColorPalette";
import Modal from "./Modal/Modal";

/**
 * Shape of the history state used to trigger the post-registration popup.
 */
interface SwissPassPostRegistrationState { "showSwissPassPostRegistration"?: boolean }

/** Popup title. */
const POPUP_TITLE = "Compte SwissPass créé";

/** Popup message explaining the user must now log in. */
const POPUP_MESSAGE = "Votre compte SwissPass a bien été enregistré. Connectez-vous maintenant pour utiliser FlowRide.";

interface SwissPassPostRegistrationAlertProps {

    /** Whether the dark UI palette is active. */
    "isDarkUi": boolean;
}

/**
 * Displays a branded modal popup when the user has just registered a SwissPass
 * account. A primary action starts the SwissPass login flow; a secondary action
 * dismisses the popup.
 */
export const SwissPassPostRegistrationAlert: React.FC<SwissPassPostRegistrationAlertProps> = ( { isDarkUi } ) => {
    const history = useHistory();
    const location = useLocation();
    const [ present ] = useIonToast();
    const palette = useMemo( () => getUIColorPalette( isDarkUi ), [ isDarkUi ] );

    const shouldShowOnMount = useMemo(
        () => typeof location.state === "object" &&
          location.state !== null &&
          ( location.state as SwissPassPostRegistrationState ).showSwissPassPostRegistration === true,
        [ location.state ],
    );

    const [ isOpen, setIsOpen ] = useState( shouldShowOnMount );

    /**
     * Clears the transient state flag as soon as the popup is shown so a page
     * refresh does not reopen it.
     */
    useEffect( () => {
        if ( shouldShowOnMount ) {
            history.replace( {
                "pathname": location.pathname,
                "search":   location.search,
                "state":    undefined,
            } );
        }
    }, [ history, location.pathname, location.search, shouldShowOnMount ] );

    /**
     * Starts the SwissPass login flow and closes the popup.
     *
     * On success the browser navigates away; on failure a bottom toast is shown.
     */
    const handleConnect = useCallback( () => {
        void startSwissPassLoginWithStateSave().catch( ( error: unknown ) => {
            const message = error instanceof Error
                ? error.message
                : "Impossible de démarrer la connexion SwissPass";
            void present( {
                message,
                "color":    "danger",
                "duration": 2500,
                "position": "bottom",
            } );
        } );
        setIsOpen( false );
    }, [ present ] );

    const handleDismiss = useCallback( () => {
        setIsOpen( false );
    }, [] );

    return (
        <Modal
            isDarkUi={isDarkUi}
            isOpen={isOpen}
            onClose={() => {
                // The popup is only closed through explicit user actions.
            }}
            showCloseButton={false}
        >
            <div className="flex flex-col items-center gap-4 text-center px-2 pt-2">
                <h2
                    className="font-bold text-lg"
                    style={{ "color": palette.brandPrimary }}
                >
                    {POPUP_TITLE}
                </h2>
                <p
                    className="text-sm leading-relaxed"
                    style={{ "color": palette.modalCardTextPrimary }}
                >
                    {POPUP_MESSAGE}
                </p>
                <div className="flex flex-col w-full gap-3 mt-2">
                    <button
                        className="w-full rounded-full px-4 py-2.5 text-sm font-medium text-white transition-colors"
                        onClick={handleConnect}
                        onMouseEnter={( event ) => {
                            event.currentTarget.style.backgroundColor = palette.swissPassButtonBackgroundHover;
                        }}
                        onMouseLeave={( event ) => {
                            event.currentTarget.style.backgroundColor = palette.swissPassButtonBackground;
                        }}
                        style={{ "backgroundColor": palette.swissPassButtonBackground }}
                        type="button"
                    >
                        Se connecter avec SwissPass
                    </button>
                    <button
                        className="w-full rounded-full px-4 py-2.5 text-sm font-medium transition-colors"
                        onClick={handleDismiss}
                        onMouseEnter={( event ) => {
                            event.currentTarget.style.backgroundColor = palette.overlayBorder;
                        }}
                        onMouseLeave={( event ) => {
                            event.currentTarget.style.backgroundColor = palette.interactiveHoverBackground;
                        }}
                        style={{
                            "backgroundColor": palette.interactiveHoverBackground,
                            "color":           palette.modalCardTextPrimary,
                        }}
                        type="button"
                    >
                        Plus tard
                    </button>
                </div>
            </div>
        </Modal>
    );
};
